import type { PitchedContent } from "@amzn/ads-discovery-theme-service-typescript-client";
import type {
    Content,
    ContentSalesforceObjectsAssociation,
} from "@amzn/ads-measurement-plm-service-type-script-client";
import { ContentType } from "@amzn/ads-measurement-plm-service-type-script-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import { useStore } from "zustand";

import { getClientId } from "../../api/adsApi/adsApi.config";
import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { getPlmApiClient } from "../../api/plmApi/plmApi";
import { authStore } from "../../store/auth";
import type { AssociatedPitchedContent } from "../../store/editContent";
import type { FeatureFlagsState } from "../../store/featureFlags";
import { FeatureFlags, FeatureFlagsStore } from "../../store/featureFlags";
import { getAdvertiserId } from "../../utils/permission.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export function usePitchedContentsAction(): [
    AsyncState<AssociatedPitchedContent[]>,
    UseAsyncActions<AssociatedPitchedContent[]>,
] {
    const accountId = getAdvertiserId();
    const flags = useStore(FeatureFlagsStore);

    const [contentsState, contentsAction] = useAsync<
        AssociatedPitchedContent[]
    >(async () => {
        const response = await (
            await getDiscoveryApiClient()
        ).getPitchedContent();

        const notInProgressContents: PitchedContent[] = response.contents ?? [];

        return handleSfActivityWorkflow(
            notInProgressContents,
            accountId,
            flags,
        );
    }, []);

    useAsyncErrorStatusNotification(contentsState);

    return [contentsState, contentsAction];
}

const handleSfActivityWorkflow = async (
    notInProgressContents: PitchedContent[],
    accountId?: string,
    flags?: FeatureFlagsState,
): Promise<AssociatedPitchedContent[]> => {
    const contentsNeedingAssociations: Content[] = notInProgressContents.map(
        mapPitchedContentToContent,
    );

    let associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();

    // Phase 2 & 3: Read ALL associations (Activity + Account/Opportunity) from PLM via AdsBlink BFF
    if (
        flags?.[FeatureFlags.PLM_WRITE_BOTH_READ_PLM] ||
        flags?.[FeatureFlags.PLM_WRITE_AND_READ_PLM]
    ) {
        if (contentsNeedingAssociations.length) {
            const associationResponse = await (
                await getBlinkApiClientV2()
            ).getContentSfAssociationViaBlink({
                userAlias: authStore.getState().username,
                clientId: getClientId(),
                contents: contentsNeedingAssociations,
            });

            associationsMap = buildAssociationsMap(
                associationResponse,
                accountId,
            );
        }

        // In Phase 2 & 3, we read from PLM, so we need to convert PLM associations to accountOpportunities format
        const mappedContents = attachAssociationsAndConvertToOpportunities(
            notInProgressContents,
            associationsMap,
        );

        const withFilteredOpps = mappedContents.map((content) =>
            filterContentForAccount(content, accountId),
        );
        return excludeIfNoOppAndNoAssoc(withFilteredOpps);
    }
    // Phase 1 & Legacy: Read Activity associations from PLM (direct or via AdsBlink BFF)
    else {
        if (contentsNeedingAssociations.length) {
            // Phase 1: Use PLM via AdsBlink BFF for Activity associations
            if (flags?.[FeatureFlags.PLM_WRITE_BOTH_READ_ACC]) {
                const associationResponse = await (
                    await getBlinkApiClientV2()
                ).getContentSfAssociationViaBlink({
                    userAlias: authStore.getState().username,
                    clientId: getClientId(),
                    contents: contentsNeedingAssociations,
                });

                associationsMap = buildAssociationsMap(
                    associationResponse,
                    accountId,
                );
            }
            // Legacy: Use direct PLM calls for Activity associations
            else {
                const associationResponse = await (
                    await getPlmApiClient()
                ).getContentSfAssociation(contentsNeedingAssociations);

                associationsMap = buildAssociationsMap(
                    associationResponse,
                    accountId,
                );
            }
        }

        // In Phase 1 & Legacy, accountOpportunities come from Theme Service (already in notInProgressContents)
        const mappedContents = attachAssociations(
            notInProgressContents,
            associationsMap,
        );

        const withFilteredOpps = mappedContents.map((content) =>
            filterContentForAccount(content, accountId),
        );
        return excludeIfNoOppAndNoAssoc(withFilteredOpps);
    }
};

const attachAssociations = (
    pitchedContents: PitchedContent[],
    associationsMap: Map<string, ContentSalesforceObjectsAssociation[]>,
): AssociatedPitchedContent[] => {
    return pitchedContents.map((pitchedContent) => {
        const contentId = pitchedContent.contentId;
        const salesforceAssociations = associationsMap.get(contentId!) ?? [];
        return {
            ...pitchedContent,
            associations: salesforceAssociations,
        };
    });
};

const attachAssociationsAndConvertToOpportunities = (
    pitchedContents: PitchedContent[],
    associationsMap: Map<string, ContentSalesforceObjectsAssociation[]>,
): AssociatedPitchedContent[] => {
    return pitchedContents.map((pitchedContent) => {
        const contentId = pitchedContent.contentId;
        const salesforceAssociations = associationsMap.get(contentId!) ?? [];

        // Convert PLM associations to accountOpportunities format for Phase 2 & 3
        const accountOpportunities = salesforceAssociations
            .filter(
                (assoc) =>
                    assoc.salesforceAssociation?.salesforceObjectType ===
                    "OPPORTUNITY",
            )
            .map((assoc) => ({
                accountId: assoc.salesforceAssociation?.salesforceAccountId,
                opportunityId: assoc.salesforceAssociation?.salesforceObjectId,
            }))
            .filter(
                (opp): opp is { accountId: string; opportunityId: string } =>
                    !!opp.accountId && !!opp.opportunityId,
            );

        return {
            ...pitchedContent,
            associations: salesforceAssociations,
            accountOpportunities:
                accountOpportunities.length > 0
                    ? accountOpportunities
                    : pitchedContent.accountOpportunities,
        };
    });
};

const filterContentForAccount = (
    content: AssociatedPitchedContent,
    accountId?: string,
): AssociatedPitchedContent => {
    if (accountId && content.accountOpportunities) {
        const filteredOpps = content.accountOpportunities.filter(
            (opportunity) => opportunity.accountId === accountId,
        );
        return { ...content, accountOpportunities: filteredOpps };
    }
    return content;
};

const excludeIfNoOppAndNoAssoc = (
    contents: AssociatedPitchedContent[],
): AssociatedPitchedContent[] => {
    return contents.filter((c) => {
        const isAccountOpportunitiesEmpty = isEmpty(c.accountOpportunities);
        const isSalesforceAssociationsEmpty = isEmpty(c.associations);
        return !isAccountOpportunitiesEmpty || !isSalesforceAssociationsEmpty;
    });
};

const buildAssociationsMap = (
    response: {
        contentSalesforceObjectAssociations?: ContentSalesforceObjectsAssociation[];
    },
    accountId?: string,
): Map<string, ContentSalesforceObjectsAssociation[]> => {
    const associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();

    response.contentSalesforceObjectAssociations
        ?.filter(
            (
                item,
            ): item is ContentSalesforceObjectsAssociation & {
                contentId: string;
            } =>
                !!item.content?.contentId && accountId !== undefined
                    ? item.salesforceAssociation?.salesforceAccountId ===
                      accountId
                    : false,
        )
        .forEach((item) => {
            const contentId = item.content?.contentId;
            const existing = associationsMap.get(contentId!) ?? [];
            existing.push(item);
            associationsMap.set(contentId!, existing);
        });
    return associationsMap;
};

const mapPitchedContentToContent = (
    pitchedContent: PitchedContent,
): Content => {
    const { contentId, contentType } = pitchedContent;
    const validContentTypes = Object.values(ContentType);

    if (contentType === "PITCH") {
        return {
            contentId,
            contentType: ContentType.PITCHED_CONTENT,
        };
    }
    if (validContentTypes.includes(contentType as ContentType)) {
        return {
            contentId,
            contentType: contentType as ContentType,
        };
    }
    return {
        contentId,
        contentType: undefined,
    };
};
